import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileDropzone } from '../components/upload/FileDropzone';
import { uploadDocument } from '../services/api';
import { BRAND_CODE, BRAND_NAME } from '../config/branding';

/* SPA-3 — Expanded agreement-type list. The team flagged that
   investor-protection clauses often live in supporting docs
   (Investors Rights, Disclosure Schedule, Voting, ROFR) rather than
   the main SPA. Without these slots the engine never sees those
   clauses and they appear as MISSING in the legal review. Mirrors
   the DD page's pattern of fixed core types + free-form "Other". */
const SPA_TYPES = [
  { value: 'SPA',                 label: 'Share Purchase Agreement',   desc: 'Purchase mechanics, R&W, closing conditions' },
  { value: 'SHA',                 label: 'Shareholders Agreement',      desc: 'Governance, board, exit, anti-dilution' },
  { value: 'InvestorsRights',     label: 'Investors Rights Agreement',  desc: 'Information rights, registration, drag/tag' },
  { value: 'DisclosureSchedule',  label: 'Disclosure Schedule',         desc: 'R&W exceptions, capitalization details' },
  { value: 'VotingAgreement',     label: 'Voting Agreement',            desc: 'Voting commitments, drag-along, ROFR' },
  { value: 'Escrow',              label: 'Escrow Agreement',            desc: 'Deposit terms, release triggers, indemnity' },
];

const REVIEW_STEPS = [
  'Document Upload',
  'Clause Detection',
  'Investor Protection Analysis',
  'Issue Classification',
  'Report Generation',
];

type FileMap = Record<string, File | null>;
type SelectedMap = Record<string, boolean>;

interface OptionalDoc {
  id: string;
  label: string;
  file: File | null;
}

export function SPAUploadPage() {
  const navigate = useNavigate();
  /* Sprint 5 — project context when rendered under
     /projects/:auditId/spa/upload. */
  const { auditId } = useParams<{ auditId?: string }>();
  const [files, setFiles] = useState<FileMap>({
    SPA: null, SHA: null, InvestorsRights: null,
    DisclosureSchedule: null, VotingAgreement: null, Escrow: null,
  });
  const [selected, setSelected] = useState<SelectedMap>({
    SPA: true, SHA: false, InvestorsRights: false,
    DisclosureSchedule: false, VotingAgreement: false, Escrow: false,
  });
  const [optionalDocs, setOptionalDocs] = useState<OptionalDoc[]>([]);
  const [dealName, setDealName] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setFile = (type: string) => (file: File | null) =>
    setFiles((prev) => ({ ...prev, [type]: file }));

  const toggleSelected = (type: string) => () =>
    setSelected((prev) => {
      const next = { ...prev, [type]: !prev[type] };
      if (!next[type]) setFiles((f) => ({ ...f, [type]: null }));
      return next;
    });

  // SPA-3 — optional-docs helpers, mirroring the DD page so analysts
  // can attach supporting docs that don't match any of the standard
  // agreement tiles (e.g. side letters, MNDA, term sheet).
  const addOptionalDoc = () => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setOptionalDocs((prev) => [...prev, { id, label: '', file: null }]);
  };
  const updateOptionalDoc = (id: string, patch: Partial<OptionalDoc>) => {
    setOptionalDocs((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  };
  const removeOptionalDoc = (id: string) => {
    setOptionalDocs((prev) => prev.filter((d) => d.id !== id));
  };

  const activeTypes = SPA_TYPES.filter((t) => selected[t.value]);
  const hasAnyType = activeTypes.length > 0;
  const allSelectedHaveFiles = hasAnyType && activeTypes.every((t) => files[t.value]);
  const missingTypes = activeTypes.filter((t) => !files[t.value]).map((t) => t.label);

  /* Sprint 5 client feedback 2026-06-20 — see DDUploadPage for context.
     Per-file upload progress; navigation after a brief settle delay. */
  type UploadItemStatus = 'pending' | 'uploading' | 'done' | 'failed';
  type UploadItem = {
    key: string;
    label: string;
    filename: string;
    status: UploadItemStatus;
    error?: string;
  };
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasAnyType) { setError('Select at least one agreement type.'); return; }
    if (!allSelectedHaveFiles) {
      setError(`Upload a file for each selected agreement type. Missing: ${missingTypes.join(', ')}.`);
      return;
    }
    setError(null);
    setIsSubmitting(true);

    const entries = activeTypes.filter((t) => files[t.value]);
    const validOptional = optionalDocs.filter((d) => d.file && d.label.trim().length > 0);

    const initialItems: UploadItem[] = [
      ...entries.map((t) => ({
        key: `m:${t.value}`,
        label: t.label,
        filename: files[t.value]!.name,
        status: 'pending' as UploadItemStatus,
      })),
      ...validOptional.map((od, i) => ({
        key: `o:${i}`,
        label: od.label || 'Optional document',
        filename: od.file!.name,
        status: 'pending' as UploadItemStatus,
      })),
    ];
    setUploadItems(initialItems);

    const setItem = (key: string, patch: Partial<UploadItem>) => {
      setUploadItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
    };

    try {
      let assessmentId: string | null = null;
      for (const t of entries) {
        const key = `m:${t.value}`;
        setItem(key, { status: 'uploading' });
        try {
          const response = await uploadDocument(files[t.value]!, {
            module_type: 'SPA',
            document_subtype: t.value,
            deal_name: dealName || undefined,
            analyst_name: reviewerName || undefined,
            assessment_id: assessmentId || undefined,
            project_audit_id: auditId || undefined,
          });
          if (!assessmentId) assessmentId = response.assessment_id;
          setItem(key, { status: 'done' });
        } catch (err: unknown) {
          const msg =
            (err as { response?: { data?: { detail?: { message?: string } } } })?.response?.data
              ?.detail?.message ?? 'Upload failed';
          setItem(key, { status: 'failed', error: typeof msg === 'string' ? msg : 'Upload failed' });
          throw err;
        }
      }
      for (const [i, od] of validOptional.entries()) {
        const key = `o:${i}`;
        setItem(key, { status: 'uploading' });
        try {
          await uploadDocument(od.file!, {
            module_type: 'SPA',
            document_subtype: 'Other',
            deal_name: dealName || undefined,
            analyst_name: reviewerName || undefined,
            assessment_id: assessmentId || undefined,
            project_audit_id: auditId || undefined,
          });
          setItem(key, { status: 'done' });
        } catch (err: unknown) {
          const msg =
            (err as { response?: { data?: { detail?: { message?: string } } } })?.response?.data
              ?.detail?.message ?? 'Upload failed';
          setItem(key, { status: 'failed', error: typeof msg === 'string' ? msg : 'Upload failed' });
          throw err;
        }
      }
      await new Promise((r) => setTimeout(r, 1200));
      if (assessmentId) {
        /* Sprint 5 fix — see DDUploadPage for the full reasoning.
           Short version: `/processing/{id}` polls per-document status
           and prematurely shows "completed" after ingestion of a
           single doc; `/assessments/{id}` polls the whole-assessment
           status that only flips after AI analysis + report
           generation finish. */
        const ret = auditId ? `?return_to=/projects/${auditId}` : '';
        navigate(`/assessments/${assessmentId}${ret}`);
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: { message?: string } } } })?.response?.data
          ?.detail?.message ?? 'Upload failed. Please try again.';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const mandatoryUploaded = Object.entries(files).filter(([k, v]) => v && selected[k]).length;
  const optionalUploaded = optionalDocs.filter((d) => d.file && d.label.trim().length > 0).length;
  const uploadCount = mandatoryUploaded + optionalUploaded;

  return (
    <div className="module-spa">
      <div className="module-page-header">
        {auditId && (
          <div
            style={{
              padding: 'var(--s3) var(--s4)',
              background: 'var(--spa-accent-bg)',
              border: '1px solid var(--spa-accent-border)',
              borderRadius: 'var(--r-md)',
              marginBottom: 'var(--s4)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: '0.9rem' }}>
              Uploading Stage 2 (SPA) documents for project{' '}
              <strong style={{ fontFamily: 'var(--font-mono)' }}>
                {auditId}
              </strong>
            </span>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => navigate(`/projects/${auditId}`)}
            >
              ← Back to project
            </button>
          </div>
        )}
        <div className="breadcrumb">
          {auditId ? (
            <>
              <a href="/projects" onClick={(e) => { e.preventDefault(); navigate('/projects'); }}>Projects</a>
              <span className="bc-sep">/</span>
              <a href={`/projects/${auditId}`} onClick={(e) => { e.preventDefault(); navigate(`/projects/${auditId}`); }}>{auditId}</a>
              <span className="bc-sep">/</span>
              <span>Stage 2 — SPA Upload</span>
            </>
          ) : (
            <>
              <a href="/dashboard" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}>Dashboard</a>
              <span className="bc-sep">/</span>
              <span>SPA Reviews</span>
              <span className="bc-sep">/</span>
              <span>New Review</span>
            </>
          )}
        </div>
        <div className="report-title-row">
          <div>
            <p className="module-eyebrow">SPA Module</p>
            <h1 className="t-h1">New SPA Legal Review</h1>
            <p className="t-xs t-muted" style={{ marginTop: 4 }}>Upload your agreement and configure the review parameters.</p>
          </div>
          <div className="workflow-indicator">
            <div className="wi-step active"><div className="wi-num">1</div><span>Upload</span></div>
            <div className="wi-line" />
            <div className="wi-step"><div className="wi-num">2</div><span>Processing</span></div>
            <div className="wi-line" />
            <div className="wi-step"><div className="wi-num">3</div><span>Report</span></div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <form onSubmit={handleSubmit}>
          <div className="upload-layout">
            <div className="upload-main">

              {/* Step 1 — Agreement Type selector */}
              <div className="card">
                <div className="card-header">
                  <div>
                    <h2 className="t-h3">Agreement Type <span style={{ color: 'var(--fail)', marginLeft: 2 }}>*</span></h2>
                    <p className="t-xs t-muted t-mt-2px">Required — drives clause detection schema</p>
                  </div>
                  <span className="badge badge-neutral">Step 1 of 3</span>
                </div>

                <div className="alert alert-warn" style={{ marginBottom: 'var(--s3)' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  <span><strong>Agreement type cannot be auto-detected</strong> — your selection drives the clause detection schema applied to this review.</span>
                </div>

                <div className="doctype-tiles">
                  {SPA_TYPES.map((t) => {
                    const isSelected = selected[t.value];
                    return (
                      <button
                        key={t.value}
                        type="button"
                        className={`doctype-tile${isSelected ? ' selected' : ''}`}
                        onClick={toggleSelected(t.value)}
                        aria-pressed={isSelected}
                      >
                        <div className="doctype-tile-body">
                          <div className="doctype-tile-title">{t.label}</div>
                          <div className="doctype-tile-desc">{t.desc}</div>
                        </div>
                        <span className={`doctype-tile-check${isSelected ? ' on' : ''}`} aria-hidden="true">
                          {isSelected && (
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 2 — Upload Agreement */}
              <div className="card">
                <div className="card-header">
                  <div>
                    <h2 className="t-h3">Upload Agreement</h2>
                    <p className="t-xs t-muted t-mt-2px">PDF or DOCX preferred — up to 50 MB</p>
                  </div>
                  <span className="badge badge-neutral">Step 2 of 3</span>
                </div>

                {activeTypes.length === 0 && (
                  <p className="t-sm t-muted" style={{ padding: 'var(--s4) 0' }}>
                    Select at least one agreement type above to enable upload.
                  </p>
                )}

                {activeTypes.map((t) => (
                  <div key={t.value} style={{ marginBottom: 'var(--s3)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span className="t-label">{t.label}</span>
                      {files[t.value] ? (
                        <span className="badge badge-pass">File ready</span>
                      ) : (
                        <span className="badge badge-neutral">No file</span>
                      )}
                    </div>
                    <FileDropzone onFile={setFile(t.value)} onClear={setFile(t.value)} />
                  </div>
                ))}

                {/* SPA-3 — analyst-supplied extras (side letters, MNDAs,
                    term sheets) with custom labels. Mirrors DD's pattern. */}
                {optionalDocs.map((doc) => (
                  <div key={doc.id} style={{ marginBottom: 'var(--s3)', paddingTop: 'var(--s3)', borderTop: '1px dashed var(--lines)' }}>
                    <div style={{ display: 'flex', gap: 'var(--s2)', alignItems: 'center', marginBottom: 6 }}>
                      <input
                        className="form-input"
                        type="text"
                        placeholder="Other agreement type (e.g. Side Letter, Term Sheet, MNDA)"
                        value={doc.label}
                        onChange={(e) => updateOptionalDoc(doc.id, { label: e.target.value })}
                        style={{ flex: 1 }}
                      />
                      {doc.file ? (
                        <span className="badge badge-pass">File ready</span>
                      ) : (
                        <span className="badge badge-neutral">No file</span>
                      )}
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => removeOptionalDoc(doc.id)}
                        aria-label="Remove optional document"
                      >
                        Remove
                      </button>
                    </div>
                    <FileDropzone
                      onFile={(f) => updateOptionalDoc(doc.id, { file: f })}
                      onClear={() => updateOptionalDoc(doc.id, { file: null })}
                    />
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); addOptionalDoc(); }}
                  style={{ marginTop: 'var(--s2)' }}
                >
                  + Add other agreement
                </button>

                <div className="alert alert-pass" style={{ marginTop: 'var(--s3)' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  <span><strong>Privileged document handling</strong> — all legal agreements are processed under strict air-gapped conditions. No data leaves the {BRAND_NAME} network.</span>
                </div>
              </div>

              {/* Step 3 — Deal Context */}
              <div className="card">
                <div className="card-header">
                  <div>
                    <h2 className="t-h3">Deal Context <span className="t-muted t-optional">(Optional)</span></h2>
                  </div>
                  <span className="badge badge-neutral">Step 3 of 3</span>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label" htmlFor="dealName">Deal name</label>
                    <input
                      className="form-input"
                      type="text"
                      id="dealName"
                      placeholder="e.g. Project Helios"
                      value={dealName}
                      onChange={(e) => setDealName(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="reviewer">Lead reviewer</label>
                    <input
                      className="form-input"
                      type="text"
                      id="reviewer"
                      placeholder="e.g. James Kim"
                      value={reviewerName}
                      onChange={(e) => setReviewerName(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {error && (
                <div className="alert alert-fail" role="alert">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {error}
                </div>
              )}

              <div className="upload-submit-row">
                <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)} disabled={isSubmitting}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={isSubmitting || !allSelectedHaveFiles}
                >
                  {isSubmitting ? (
                    <><span className="spinner" /> Uploading {uploadCount} file{uploadCount !== 1 ? 's' : ''}…</>
                  ) : (
                    <>
                      Start Processing{uploadCount > 0 ? ` (${uploadCount} file${uploadCount !== 1 ? 's' : ''})` : ''}
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                    </>
                  )}
                </button>
              </div>

              {uploadItems.length > 0 && (
                <div
                  className="card"
                  style={{
                    marginTop: 'var(--s4)',
                    padding: 'var(--s4)',
                  }}
                  aria-live="polite"
                >
                  <div
                    style={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      marginBottom: 'var(--s3)',
                      color: 'var(--text)',
                    }}
                  >
                    Upload progress
                    {' — '}
                    <span style={{ color: 'var(--muted)', fontWeight: 400 }}>
                      {uploadItems.filter((i) => i.status === 'done').length}{' '}
                      of {uploadItems.length} uploaded
                    </span>
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
                    {uploadItems.map((it) => (
                      <li
                        key={it.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '6px 10px',
                          background:
                            it.status === 'done'
                              ? 'color-mix(in srgb, var(--pass) 8%, transparent)'
                              : it.status === 'failed'
                              ? 'color-mix(in srgb, var(--fail) 10%, transparent)'
                              : 'var(--surface-alt)',
                          borderRadius: 'var(--r-sm)',
                          fontSize: '0.85rem',
                        }}
                      >
                        <span style={{ display: 'inline-flex', width: 18, justifyContent: 'center' }}>
                          {it.status === 'uploading' && <span className="spinner spinner-sm" />}
                          {it.status === 'done' && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--pass)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                          {it.status === 'failed' && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--fail)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          )}
                          {it.status === 'pending' && (
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--lines)' }} />
                          )}
                        </span>
                        <span style={{ fontWeight: 500 }}>{it.label}</span>
                        <span style={{ color: 'var(--muted)', fontSize: '0.78rem', flex: 1 }}>
                          {it.filename}
                        </span>
                        <span
                          style={{
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            color:
                              it.status === 'done' ? 'var(--pass)'
                              : it.status === 'failed' ? 'var(--fail)'
                              : it.status === 'uploading' ? 'var(--info)'
                              : 'var(--muted)',
                          }}
                        >
                          {it.status === 'uploading' ? 'Uploading…' : it.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {uploadItems.every((i) => i.status === 'done') && (
                    <div
                      style={{
                        marginTop: 'var(--s3)',
                        fontSize: '0.82rem',
                        color: 'var(--pass)',
                        fontWeight: 600,
                      }}
                    >
                      ✓ All files uploaded — opening live processing page…
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="upload-sidebar">
              <div className="card card-sm">
                <p className="t-label t-label-mb3">Review steps</p>
                <div className="upload-steps-list">
                  {REVIEW_STEPS.map((step, i) => (
                    <div key={i} className={`upload-step-row ${i === 0 ? 'active' : 'inactive'}`}>
                      <div className={`upload-step-circle-sm ${i === 0 ? 'active' : 'inactive'}`}>{i + 1}</div>
                      {step}
                    </div>
                  ))}
                </div>
              </div>
              <div className="card card-sm">
                <p className="t-label t-label-mb3">Template applied</p>
                <div className="upload-sidebar-bench">
                  <div className="upload-bench-id">{BRAND_CODE}-SPA-2024</div>
                  <p className="t-xs t-muted" style={{ margin: 0 }}>Standard SPA review template · 2024 edition</p>
                </div>
              </div>
              <div className="card card-sm">
                <p className="t-label t-label-mb3">Supported formats</p>
                <div className="upload-format-list">
                  {['PDF','DOCX'].map((fmt) => (
                    <div key={fmt} className="upload-format-row">
                      <span className="upload-format-key">{fmt}</span>
                      <span className="t-muted t-xs">Preferred · Up to 50 MB</span>
                    </div>
                  ))}
                </div>
                <p className="t-xs t-muted" style={{ marginTop: 'var(--s3)', lineHeight: 1.4 }}>
                  <strong>Clause schema:</strong> Agreement type determines which mandatory clause set is checked. SPA checks 10 required sections; SHA checks governance and exit provisions; Escrow checks deposit mechanics.
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
